import React, { useState, useRef } from 'react';
import { X, Upload, Image as ImageIcon } from 'lucide-react';
import { createStory } from '../lib/api';
import ConfirmModal from './ConfirmModal';

const CreateStoryModal = ({ isOpen, onClose, onStoryCreated }) => {
  const [textContent, setTextContent] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: ''
  });
  const fileInputRef = useRef(null);

  const showModal = (type, title, message) => {
    setModalConfig({
      isOpen: true,
      type,
      title,
      message
    });
  };

  const closeModal = () => {
    setModalConfig(prev => ({ ...prev, isOpen: false }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validar tipo de arquivo
      if (!file.type.startsWith('image/')) {
        showModal('warning', 'Arquivo inválido', 'Por favor, selecione apenas arquivos de imagem');
        return;
      }
      
      // Validar tamanho (máximo 10MB)
      if (file.size > 10 * 1024 * 1024) {
        showModal('warning', 'Arquivo muito grande', 'A imagem é muito grande. Tamanho máximo: 10MB');
        return;
      }

      setImageFile(file);
      
      // Criar preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar que tem pelo menos texto ou imagem
    if (!textContent.trim() && !imageFile) {
      showModal('warning', 'Conteúdo necessário', 'Por favor, adicione texto ou uma imagem');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      
      if (textContent.trim()) {
        formData.append('text_content', textContent.trim());
      }
      
      if (imageFile) {
        formData.append('image_file', imageFile);
      }

      await createStory(formData);
      
      // Limpar formulário
      setTextContent('');
      setImageFile(null);
      setImagePreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      onStoryCreated();
      onClose();
    } catch (error) {
      console.error('Erro ao criar story:', error);
      showModal('error', 'Erro ao criar story', error.message || 'Erro ao criar story. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-white/20 rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-slate-900 border-b border-white/10 p-6 flex items-center justify-between">
          <h2 className="text-2xl font-black flex items-center gap-2">
            ✨ Criar Story
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
            disabled={submitting}
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Texto */}
          <div>
            <label className="block text-sm font-bold mb-2">Texto (opcional)</label>
            <textarea
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              placeholder="O que está acontecendo?"
              rows={4}
              className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-3 outline-none focus:border-purple-500/50 transition-colors resize-none text-white"
              disabled={submitting}
            />
          </div>

          {/* Upload de Imagem */}
          <div>
            <label className="block text-sm font-bold mb-2">Imagem (opcional)</label>
            {!imagePreview ? (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-white/20 rounded-md p-8 text-center cursor-pointer hover:border-purple-500/50 transition-colors"
              >
                <ImageIcon className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p className="text-sm text-gray-400 mb-1">Clique para selecionar uma imagem</p>
                <p className="text-xs text-gray-500">PNG, JPG, GIF até 10MB</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                  disabled={submitting}
                />
              </div>
            ) : (
              <div className="relative">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-full rounded-md max-h-64 object-cover"
                />
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-2 transition-colors"
                  disabled={submitting}
                >
                  <X size={18} />
                </button>
              </div>
            )}
          </div>

          {/* Validação */}
          {!textContent.trim() && !imageFile && (
            <p className="text-sm text-yellow-400">
              ⚠️ Você precisa adicionar texto ou uma imagem
            </p>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-white/10">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-md font-bold transition-colors"
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting || (!textContent.trim() && !imageFile)}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-md font-bold hover:shadow-[0_0_20px_rgba(139,92,246,0.5)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Publicando...' : 'Publicar Story'}
            </button>
          </div>
        </form>
      </div>

      {/* Modal de confirmação/erro */}
      <ConfirmModal
        isOpen={modalConfig.isOpen}
        onClose={closeModal}
        type={modalConfig.type}
        title={modalConfig.title}
        message={modalConfig.message}
      />
    </div>
  );
};

export default CreateStoryModal;

